import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, getWeeklyRecap } from 'wasp/client/operations';
import { Link, routes } from 'wasp/client/router';
import { RecapEmptyState } from './components/RecapEmptyState';
import { RecapArticleRow } from './components/RecapArticleRow';
import { StreakBadge } from '../user/StreakBadge';

function useCountUp(target: number, duration = 600): number {
  const [value, setValue] = useState(0);
  const prevTarget = useRef<number | null>(null);
  const currentValue = useRef(0);

  useEffect(() => {
    if (target === prevTarget.current) return;
    prevTarget.current = target;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReducedMotion) {
      currentValue.current = target;
      setValue(target);
      return;
    }

    const from = currentValue.current;
    const start = performance.now();
    let rafId: number;

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const next = Math.round(from + (target - from) * eased);
      currentValue.current = next;
      setValue(next);
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration]);

  return value;
}

function formatDateRange(weekStart: string, weekEnd: string): string {
  const start = new Date(weekStart);
  const end = new Date(weekEnd);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString(
    'en-US',
    opts,
  )} \u2013 ${end.toLocaleDateString('en-US', opts)}`;
}

export function RecapPage() {
  const { data, isLoading, error, refetch } = useQuery(
    getWeeklyRecap,
    undefined,
    { staleTime: 5 * 60 * 1000 },
  );

  const totalEngagements = data
    ? data.bookmarksThisWeek +
      data.likesThisWeek +
      data.discussionsStartedThisWeek +
      data.commentsThisWeek
    : 0;
  const animatedTotal = useCountUp(totalEngagements);

  return (
    <>
      <Helmet>
        <title>Your Week in Good News - Optimistic News</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="py-8 lg:py-12">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              <span className="text-primary">Your Week</span>
              <span className="text-neutral-900 dark:text-white">
                {' '}
                in Good News
              </span>
            </h1>
            {data && (
              <p className="mt-1 text-neutral-500 dark:text-neutral-400">
                {formatDateRange(data.weekStart, data.weekEnd)}
              </p>
            )}
          </div>

          {/* Loading skeleton */}
          {isLoading && (
            <div
              className="space-y-10"
              role="status"
              aria-label="Loading your weekly recap"
            >
              <div className="flex flex-col items-center gap-3 animate-pulse">
                <div className="h-16 w-28 rounded-lg bg-neutral-200 dark:bg-neutral-700" />
                <div className="h-5 w-48 rounded bg-neutral-200 dark:bg-neutral-700" />
                <div className="h-4 w-72 rounded bg-neutral-200 dark:bg-neutral-700" />
              </div>
              <div className="rounded-2xl bg-neutral-100 p-8 dark:bg-neutral-800/50 animate-pulse">
                <div className="h-5 w-40 rounded mb-4 bg-neutral-200 dark:bg-neutral-700" />
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="h-16 w-24 rounded-lg bg-neutral-200 dark:bg-neutral-700" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-3/4 rounded bg-neutral-200 dark:bg-neutral-700" />
                        <div className="h-3 w-1/2 rounded bg-neutral-200 dark:bg-neutral-700" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="mb-2 text-lg font-semibold text-danger-600 dark:text-danger-400">
                Failed to load your recap
              </p>
              <p className="mb-6 text-sm text-neutral-500 dark:text-neutral-400">
                {error.message}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
              >
                Retry
              </button>
            </div>
          )}

          {/* Empty state */}
          {data && !isLoading && !error && data.isEmpty && <RecapEmptyState />}

          {/* Active state */}
          {data && !isLoading && !error && !data.isEmpty && (
            <>
              {/* Engagement summary */}
              <div className="mb-10 flex flex-col items-center text-center">
                {totalEngagements >= 3 ? (
                  <>
                    <p className="text-5xl font-bold tracking-tight text-primary sm:text-6xl">
                      {animatedTotal}
                    </p>
                    <p className="mt-1 text-lg text-neutral-600 dark:text-neutral-300">
                      engagements this week
                    </p>
                  </>
                ) : (
                  <p className="text-lg text-neutral-600 dark:text-neutral-300">
                    You&apos;re getting started &mdash; keep engaging!
                  </p>
                )}
                <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
                  {data.likesThisWeek > 0 && (
                    <span className="text-neutral-500 dark:text-neutral-400">
                      <span className="font-semibold text-neutral-700 dark:text-neutral-200">
                        {data.likesThisWeek}
                      </span>{' '}
                      {data.likesThisWeek === 1 ? 'like' : 'likes'}
                    </span>
                  )}
                  {data.bookmarksThisWeek > 0 && (
                    <span className="text-neutral-500 dark:text-neutral-400">
                      <span className="font-semibold text-neutral-700 dark:text-neutral-200">
                        {data.bookmarksThisWeek}
                      </span>{' '}
                      {data.bookmarksThisWeek === 1 ? 'save' : 'saves'}
                    </span>
                  )}
                  {data.topCategory && (
                    <span className="text-neutral-500 dark:text-neutral-400">
                      Led by{' '}
                      <span className="font-semibold text-neutral-700 dark:text-neutral-200">
                        {data.topCategory}
                      </span>
                    </span>
                  )}
                  {data.currentStreak > 0 && (
                    <span className="inline-flex items-center gap-1 text-neutral-500 dark:text-neutral-400">
                      <StreakBadge
                        streakCount={data.currentStreak}
                        className="text-sm"
                      />
                      <span>day streak</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Content grid — articles main, sidebar supporting */}
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_260px]">
                {/* Main column — article sections */}
                <div className="space-y-8">
                  {/* Liked articles */}
                  {data.topLikedArticles.length > 0 && (
                    <section className="rounded-2xl bg-neutral-50 p-5 dark:bg-neutral-800/40 sm:p-6">
                      <h3 className="mb-4 text-lg font-semibold text-neutral-900 dark:text-white">
                        Articles you loved
                      </h3>
                      <div className="space-y-1">
                        {data.topLikedArticles.map((article) => (
                          <RecapArticleRow key={article.id} article={article} />
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Bookmarked articles */}
                  {data.topBookmarkedArticles.length > 0 && (
                    <section className="rounded-2xl bg-neutral-50 p-5 dark:bg-neutral-800/40 sm:p-6">
                      <h3 className="mb-4 text-lg font-semibold text-neutral-900 dark:text-white">
                        Saved for later
                      </h3>
                      <div className="space-y-1">
                        {data.topBookmarkedArticles.map((article) => (
                          <RecapArticleRow key={article.id} article={article} />
                        ))}
                      </div>
                    </section>
                  )}
                </div>

                {/* Sidebar — community + topics */}
                <aside className="space-y-8 lg:border-l lg:border-neutral-200/60 lg:pl-8 dark:lg:border-neutral-700/40">
                  {/* Community */}
                  <section>
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                      Community
                    </h3>
                    {data.discussionsStartedThisWeek === 0 &&
                    data.commentsThisWeek === 0 ? (
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        Jump into a discussion &mdash; your takes are welcome
                        here.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {data.discussionsStartedThisWeek > 0 && (
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-neutral-900 dark:text-white">
                              {data.discussionsStartedThisWeek}
                            </span>
                            <span className="text-sm text-neutral-500 dark:text-neutral-400">
                              {data.discussionsStartedThisWeek === 1
                                ? 'discussion started'
                                : 'discussions started'}
                            </span>
                          </div>
                        )}
                        {data.commentsThisWeek > 0 && (
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-neutral-900 dark:text-white">
                              {data.commentsThisWeek}
                            </span>
                            <span className="text-sm text-neutral-500 dark:text-neutral-400">
                              {data.commentsThisWeek === 1
                                ? 'comment posted'
                                : 'comments posted'}
                            </span>
                          </div>
                        )}
                        {data.topDiscussions.length > 0 && (
                          <div className="mt-3 space-y-1">
                            {data.topDiscussions.map((disc) => (
                              <Link
                                key={disc.id}
                                to={routes.DiscussionRoute.to}
                                params={{ discussionId: disc.id }}
                                className="group flex gap-3 rounded-lg p-3 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-700/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  aria-hidden="true"
                                  className="mt-0.5 flex-shrink-0 text-neutral-400 dark:text-neutral-500"
                                >
                                  <path
                                    d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <div className="min-w-0">
                                  <p className="line-clamp-2 text-sm font-medium text-neutral-900 group-hover:text-primary dark:text-white dark:group-hover:text-primary">
                                    {disc.title}
                                  </p>
                                  <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                                    {disc.commentCount}{' '}
                                    {disc.commentCount === 1
                                      ? 'comment'
                                      : 'comments'}
                                    {disc.likeCount > 0 && (
                                      <>
                                        {' '}
                                        &middot; {disc.likeCount}{' '}
                                        {disc.likeCount === 1
                                          ? 'like'
                                          : 'likes'}
                                      </>
                                    )}
                                  </p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </section>

                  {/* Topics */}
                  {data.categoryBreakdown.length > 0 && (
                    <section>
                      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                        Your topics
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {data.categoryBreakdown.map((cat) => (
                          <span
                            key={cat.category}
                            className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                          >
                            {cat.category}
                            <span className="text-xs text-primary-500 dark:text-primary-300">
                              {cat.count}
                            </span>
                          </span>
                        ))}
                      </div>
                    </section>
                  )}
                </aside>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
